/**
 * =============================================================================
 * SPEQ55 RFP Pro - Cost Monitoring Dashboard (Admin)
 * =============================================================================
 *
 * WHAT THIS FILE DOES
 * -------------------
 * Admin-only dashboard showing LLM and infrastructure cost attribution.
 * Displays three slices of the same underlying cost data:
 *   1. Summary cards: total spend, token counts, call counts
 *   2. Daily breakdown: time-series of spend per day
 *   3. Per-user breakdown: which users are driving the cost
 *
 * WHY IT EXISTS
 * -------------
 * Gemini calls are the dominant variable cost in the product. Without a
 * visible cost dashboard we'd only notice runaway spend when the monthly
 * Google bill arrived. This page lets admin_sales / super_admin proactively
 * spot anomalies and correlate them with pricing changes (see the sister
 * PricingManagement page) or individual users running large analyses.
 *
 * DEPENDENCIES
 * ------------
 *   ../../api/costs - axios wrappers for GET /api/admin/costs/* endpoints
 *
 * DEPENDED ON BY
 * --------------
 *   frontend/src/App.jsx - mounted at /app/admin/costs
 *                          (super_admin + admin_sales only)
 *
 * @module pages/admin/CostMonitoring
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import * as costsApi from '../../api/costs';

export default function CostMonitoring() {
  const [summary, setSummary] = useState(null);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  const [userBreakdown, setUserBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const MONTHLY_BUDGET = 150;

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, dailyRes, userRes] = await Promise.all([
        costsApi.getCostSummary(),
        costsApi.getDailyCostBreakdown(30),
        costsApi.getUserCostBreakdown(),
      ]);

      setSummary(summaryRes.summary);
      setDailyBreakdown(dailyRes.breakdown);
      setUserBreakdown(userRes.breakdown);
    } catch {
      setError('Failed to load cost monitoring data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading cost data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  const percentUsed = (summary.totalCost / MONTHLY_BUDGET) * 100;
  const alertLevel =
    percentUsed >= 90
      ? 'critical'
      : percentUsed >= 75
        ? 'warning'
        : percentUsed >= 50
          ? 'info'
          : 'normal';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cost Monitoring Dashboard</h1>
        <p className="text-gray-600 mt-1">Track API usage and billing guardrails</p>
      </div>

      {/* Budget Overview */}
      <div
        className={`p-6 rounded-lg mb-6 ${
          alertLevel === 'critical'
            ? 'bg-red-50 border-2 border-red-500'
            : alertLevel === 'warning'
              ? 'bg-yellow-50 border-2 border-yellow-500'
              : alertLevel === 'info'
                ? 'bg-blue-50 border-2 border-blue-500'
                : 'bg-green-50 border-2 border-green-500'
        }`}
      >
        <h2 className="text-xl font-semibold mb-4">Monthly Budget Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-sm text-gray-600">Total Spent</p>
            <p className="text-2xl font-bold">${summary.totalCost.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Budget</p>
            <p className="text-2xl font-bold">${MONTHLY_BUDGET.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Remaining</p>
            <p className="text-2xl font-bold">${(MONTHLY_BUDGET - summary.totalCost).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Percent Used</p>
            <p className="text-2xl font-bold">{percentUsed.toFixed(1)}%</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-gray-200 rounded-full h-4">
          <div
            className={`h-4 rounded-full transition-all ${
              alertLevel === 'critical'
                ? 'bg-red-500'
                : alertLevel === 'warning'
                  ? 'bg-yellow-500'
                  : alertLevel === 'info'
                    ? 'bg-blue-500'
                    : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          />
        </div>

        {/* Alert messages */}
        {alertLevel === 'critical' && (
          <div className="mt-4 text-red-700 font-medium">
            ⚠️ Critical: Budget exceeded! Review usage immediately.
          </div>
        )}
        {alertLevel === 'warning' && (
          <div className="mt-4 text-yellow-700 font-medium">
            ⚠️ Warning: Approaching budget limit. Monitor usage closely.
          </div>
        )}
        {alertLevel === 'info' && (
          <div className="mt-4 text-blue-700 font-medium">ℹ️ Info: 50% of budget used.</div>
        )}
      </div>

      {/* Service Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Cost by Service</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600">Gemini API</p>
            <p className="text-xl font-bold text-blue-600">${summary.geminiCost.toFixed(2)}</p>
            <p className="text-sm text-gray-500 mt-1">
              {summary.totalTokens.toLocaleString()} tokens
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Analyses</p>
            <p className="text-xl font-bold text-purple-600">{summary.totalAnalyses}</p>
            <p className="text-sm text-gray-500 mt-1">This month</p>
          </div>
          <div className="border rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Requests</p>
            <p className="text-xl font-bold text-green-600">{summary.totalRequests}</p>
            <p className="text-sm text-gray-500 mt-1">API calls</p>
          </div>
        </div>
      </div>

      {/* Daily Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Daily Cost Trend (Last 30 Days)</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Gemini
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Total
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Analyses
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Tokens
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {dailyBreakdown.slice(0, 10).map((day) => (
                <tr key={day.summary_date} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    {new Date(day.summary_date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    ${parseFloat(day.gemini_cost_usd || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-medium">
                    ${parseFloat(day.total_cost_usd || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right">{day.total_analyses || 0}</td>
                  <td className="px-4 py-2 text-sm text-right">
                    {parseInt(day.total_tokens || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Users by Cost */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Top Users by Cost (This Month)</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  User
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Analyses
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Tokens
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Cost
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {userBreakdown.slice(0, 10).map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    <div>
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-4 py-2 text-sm text-right">{user.analysis_count || 0}</td>
                  <td className="px-4 py-2 text-sm text-right">
                    {parseInt(user.total_tokens || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-medium">
                    ${parseFloat(user.total_cost || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
